import { useState, useEffect } from 'react';
import './App.css';
import { CanyonSidebar } from './components/CanyonSidebar/index';
import { GoogleSignInButton } from './components/GoogleSignInButton/index';
import { Dashboard } from './pages/Dashboard/index';
import { Quotes } from './pages/Quotes/index';
import { CreateQuote } from './pages/CreateQuote/index';
import { Insights } from './pages/Insights/index';
import { QuoteProvider } from './contexts/QuoteContext';

export const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedQuoteId, setSelectedQuoteId] = useState<string | null>(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/auth/status');
      if (response.ok) { // for now
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    console.log(`Handling Google Login`);
    if (process.env.NODE_ENV === 'development') {
      window.location.href = 'http://localhost:3003/auth/google';
    } else {
      window.location.href = '/auth/google';
    }
  };

  const handleLogout = () => {
    window.location.href = '/logout';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <h2>🏔️ Canyon</h2>
        <p>Loading...</p>
      </div>
    );
  }

  // Public home page for non-authenticated users
  if (!user) {
    return (
      <div className="min-h-screen flex w-full bg-canyon-subtle">
        <main className="flex-1 overflow-hidden">
          {/* Header */}
          <header className="h-16 bg-white/80 backdrop-blur-sm border-b border-border/50 flex items-center px-6 sticky top-0 z-10">
            <div className="flex-1">
              <h1 className="text-xl font-bold">🏔️ Canyon</h1>
            </div>
            <div className="flex items-center gap-4">
              <button className="ghost-button">Features</button>
              <button className="ghost-button">Pricing</button>
              <GoogleSignInButton />
            </div>
          </header>

          {/* Hero Section */}
          <section className="hero-section">
            <div className="text-center max-w-3xl">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                Create Quotes in{' '}
                <span className="text-amber-500">Seconds</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                Canyon.ai helps you generate professional quotes and get insights faster.
              </p>
              
              <div className="flex gap-4 justify-center">
                <button onClick={handleGoogleLogin} className="primary-button">
                  🚀 Get Started
                </button>
              </div>

              {/* Features Preview */}
              <div className="feature-grid mt-16">
                <div className="feature-card">
                  <div className="feature-icon">⚡</div>
                  <h3>Fast Quote Creation</h3>
                  <p>Generate professional quotes in seconds</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">📊</div>
                  <h3>Smart Workflows</h3>
                  <p>Automated approval processes</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">🔒</div>
                  <h3>Secure & Reliable</h3>
                  <p>Source: Trust us</p>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  // Private app for authenticated users
  return (
    <QuoteProvider user={user}>
      <div className="min-h-screen flex w-full">
        <CanyonSidebar 
          currentPage={currentPage} 
          onPageChange={setCurrentPage} 
          user={user} 
          onLogout={handleLogout} 
        />
        <main className="flex-1 overflow-hidden">
          <div className="p-6">
            {currentPage === 'dashboard' && <Dashboard user={user} onNavigate={setCurrentPage} />}
            {currentPage === 'quotes' && <Quotes user={user} onNavigate={setCurrentPage} selectedQuoteId={selectedQuoteId} onQuoteSelect={setSelectedQuoteId} />}
            {currentPage === 'create-quote' && <CreateQuote user={user} onNavigate={setCurrentPage} onQuoteCreated={setSelectedQuoteId} />}
            {currentPage === 'insights' && <Insights />}
          </div>
        </main>
      </div>
    </QuoteProvider>
  );
};

export default App;